import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import { usePaymentDetail } from "../api/paymentQueries";
import { useApprovePayment, useRejectPayment } from "../api/paymentMutations";
import { paymentKeys } from "../api/queryKeys";
import { useInvoiceDetail, usePODetails } from "../../InvoiceManagement/v2/api/invoiceQueries";
import { useToast } from "../../InvoiceManagement/hooks/useToast";
import PaymentDetailContent from "../components/PaymentDetailContent";
import RejectModal from "../../InvoiceManagement/v2/modals/RejectModal";
import { ACTIONABLE_STATUSES } from "../types/common";

const BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "13px",
  height: 32,
  borderRadius: "4px",
  boxShadow: "none",
} as const;

interface PaymentDetailModalProps {
  open: boolean;
  paymentId: number | null;
  onClose: () => void;
  onActioned?: (id: number) => void;
}

export default function PaymentDetailModal({ open, paymentId, onClose, onActioned }: PaymentDetailModalProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [rejectOpen, setRejectOpen] = useState(false);

  const id = paymentId ?? 0;
  const { data, isLoading, isError } = usePaymentDetail(id);

  const invoiceId =
    data?.request.owner_type === "INVOICE" ? Number(data.request.owner_reference) : 0;
  const { data: invoiceDetail } = useInvoiceDetail(invoiceId);

  const poCode = invoiceDetail?.invoice?.po_code ?? "";
  const { data: poData } = usePODetails(poCode);
  // NOTE: owner_type === "PO" requests carry a PO *id* (numeric) in owner_reference, not a po_code string,
  // so usePODetails cannot be used for them. PO Code / Vendor / PO Value now come from the `owner` field
  // on GET /payment/:id (QET-545); the Customer row remains unavailable for PO-owner requests.

  const approveMutation = useApprovePayment();
  const rejectMutation = useRejectPayment();
  const isActionLoading = approveMutation.isLoading || rejectMutation.isLoading;

  const isActionable = data ? ACTIONABLE_STATUSES.has(data.request.request_status) : false;

  const reference =
    data?.request.owner_type === "PO"
      ? (data?.owner?.po_code ?? data?.request.owner_reference ?? "—")
      : (invoiceDetail?.invoice?.invoice_no ?? data?.request.owner_reference ?? "—");

  const handleApprove = () => {
    approveMutation.mutate(id, {
      onSuccess: () => {
        onActioned?.(id);
        showToast({ type: "success", message: "Payment approved" });
        queryClient.invalidateQueries(paymentKeys.detail(id));
        queryClient.invalidateQueries(paymentKeys.all);
        onClose();
      },
      onError: (error: any) => {
        showToast({ type: "error", message: error?.message ?? "Failed to approve payment" });
      },
    });
  };

  const handleReject = (reason: string) => {
    rejectMutation.mutate({ id, reason }, {
      onSuccess: () => {
        onActioned?.(id);
        setRejectOpen(false);
        showToast({ type: "success", message: "Payment rejected" });
        queryClient.invalidateQueries(paymentKeys.detail(id));
        queryClient.invalidateQueries(paymentKeys.all);
        onClose();
      },
      onError: (error: any) => {
        showToast({ type: "error", message: error?.message ?? "Failed to reject payment" });
      },
    });
  };

  const handleClose = () => {
    if (isActionLoading) return;
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography fontSize="16px" fontWeight={600}>
            Payment — {reference}
          </Typography>
          <IconButton size="small" onClick={handleClose} disabled={isActionLoading} aria-label="Close">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isLoading && (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </Box>
          )}
          {isError && (
            <Box py={4} textAlign="center">
              <Typography color="error">Failed to load payment details.</Typography>
            </Box>
          )}
          {data && (
            <PaymentDetailContent
              request={data.request}
              executions={data.executions ?? []}
              invoiceDetail={invoiceDetail}
              poDetails={poData?.po_list?.[0]}
              owner={data.owner ?? null}
              deductions={data.deductions}
              grossAmount={data.gross_amount}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button variant="outlined" size="small" onClick={handleClose} disabled={isActionLoading} sx={BTN_SX}>
            Close
          </Button>
          {isActionable && (
            <>
              <Button
                variant="contained"
                size="small"
                disabled={isActionLoading}
                startIcon={isActionLoading ? <CircularProgress size={14} /> : <Close sx={{ fontSize: 16 }} />}
                onClick={() => setRejectOpen(true)}
                sx={{ ...BTN_SX, color: "#333", backgroundColor: "#E6E6E6", "&:hover": { backgroundColor: "#D6D6D6", boxShadow: "none" } }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={isActionLoading}
                startIcon={isActionLoading ? <CircularProgress size={14} /> : <Check sx={{ fontSize: 16 }} />}
                onClick={handleApprove}
                sx={{ ...BTN_SX, backgroundColor: "#3655F2", "&:hover": { backgroundColor: "#3655F2", boxShadow: "none" } }}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <RejectModal
        open={rejectOpen}
        loading={rejectMutation.isLoading}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleReject}
      />
    </>
  );
}
